import { and, eq } from 'drizzle-orm'
import { inngest } from '@/lib/inngest'
import { db as defaultDb } from '@/db'
import { aiArtifacts, auditLog, projects, tenantConfigs, ticketMessages, tickets } from '@/db/schema'
import { transition } from '@/lib/ticket-state-machine'
import { buildSummarizationPrompt } from '../../prompts/summarization'
import { callSummarizationLLM, type AIGatewayConfig, type SummaryResult } from '@/lib/ai-gateway'
import { publishInboxTicket } from '@/lib/inbox-events'
import type { PostgresJsDatabase } from 'drizzle-orm/postgres-js'
import type * as schema from '@/db/schema'

type DB = PostgresJsDatabase<typeof schema>

export type SummarizeTicketInput = {
  ticketId: string
  tenantId: string
  messageId: string
  ticketVersion: number
}

export async function runSummarization(
  input: SummarizeTicketInput,
  options?: {
    db?: DB
    callLLM?: (config: AIGatewayConfig, system: string, user: string) => Promise<SummaryResult>
    publishEvent?: typeof publishInboxTicket
  },
): Promise<{ outcome: 'AWAITING_EXPERT' | 'STALE'; title?: string }> {
  const db = (options?.db ?? defaultDb) as DB
  const llm = options?.callLLM ?? callSummarizationLLM
  const publishEvent = options?.publishEvent ?? publishInboxTicket

  const [ticket] = await db
    .select()
    .from(tickets)
    .where(and(eq(tickets.id, input.ticketId), eq(tickets.tenantId, input.tenantId)))
    .limit(1)

  if (!ticket) throw new Error(`Ticket not found: ${input.ticketId}`)

  if (ticket.version !== input.ticketVersion || ticket.status !== 'TRIAGED') {
    return { outcome: 'STALE' }
  }

  const [config] = await db
    .select()
    .from(tenantConfigs)
    .where(eq(tenantConfigs.tenantId, input.tenantId))
    .limit(1)

  if (!config) throw new Error(`Tenant config not found for tenant: ${input.tenantId}`)

  const messages = await db
    .select({
      role: ticketMessages.role,
      body: ticketMessages.bodySanitized,
    })
    .from(ticketMessages)
    .where(eq(ticketMessages.ticketId, input.ticketId))
    .orderBy(ticketMessages.createdAt)

  let projectName: string | undefined
  if (ticket.projectId) {
    const [project] = await db
      .select({ name: projects.name })
      .from(projects)
      .where(eq(projects.id, ticket.projectId))
      .limit(1)
    projectName = project?.name
  }

  const { system, user } = buildSummarizationPrompt({
    referenceNumber: ticket.referenceNumber,
    messages,
    projectName,
    issueCategory: ticket.issueCategory ?? undefined,
    affectedSystem: ticket.affectedSystem ?? undefined,
    actionRequested: ticket.actionRequested ?? undefined,
  })

  let result: SummaryResult
  try {
    result = await llm(
      {
        provider: config.aiProvider,
        model: config.aiModel,
        temperature: config.aiTemperature,
        dataProcessingConsent: config.dataProcessingConsent,
      },
      system,
      user,
    )
  } catch (err) {
    result = buildFallbackSummary(ticket.referenceNumber, messages, err)
  }

  const nextStatus = transition(ticket.status, 'AWAITING_EXPERT')
  const now = new Date()

  await db.insert(aiArtifacts).values({
    tenantId: input.tenantId,
    ticketId: input.ticketId,
    sourceMessageId: input.messageId,
    triggerEvent: 'ticket/triaged',
    artifactType: 'summary',
    content: result.summary,
    contentJson: JSON.stringify(result),
    modelUsed: config.aiModel,
    promptVersion: '1.0.0',
    validationStatus: 'valid',
  })

  const [updatedTicket] = await db
    .update(tickets)
    .set({
      status: nextStatus,
      title: result.title,
      aiProcessingState: 'idle',
      version: ticket.version + 1,
      updatedAt: now,
    })
    .where(and(eq(tickets.id, input.ticketId), eq(tickets.version, ticket.version)))
    .returning()

  if (!updatedTicket) return { outcome: 'STALE' }

  await db.insert(auditLog).values({
    tenantId: input.tenantId,
    actorId: null,
    actorType: 'ai_agent',
    action: 'ticket.summarized',
    entityType: 'ticket',
    entityId: input.ticketId,
    payload: {
      from: ticket.status,
      to: nextStatus,
      hook: 'summarization',
      reasoning_trace: result.reasoning_trace,
    },
  })

  publishEvent({
    tenantId: input.tenantId,
    ticketId: input.ticketId,
    title: result.title,
    status: 'AWAITING_EXPERT',
    updatedAt: updatedTicket.updatedAt.toISOString(),
  })

  return { outcome: 'AWAITING_EXPERT', title: result.title }
}

function buildFallbackSummary(
  referenceNumber: string,
  messages: Array<{ role: string; body: string }>,
  err: unknown,
): SummaryResult {
  const firstLineManagerMessage = messages.find((message) => message.role === 'line_manager')
  const sourceText = firstLineManagerMessage?.body ?? 'No line manager message was available.'
  const compactText = sourceText.length > 500 ? `${sourceText.slice(0, 497)}...` : sourceText
  const errorMessage = err instanceof Error ? err.message : 'Unknown summarization error'

  return {
    title: `Manual review needed for ${referenceNumber}`,
    summary: `AI summarization was unavailable. Latest available submission: ${compactText}`,
    reasoning_trace: `Fallback summary used because Hook 4 failed: ${errorMessage}`,
  }
}

export const summarizationFn = inngest.createFunction(
  { id: 'summarization', retries: 3, triggers: [{ event: 'ticket/triaged' }] },
  async ({ event, step }) => {
    return await step.run('run-summarization', () =>
      runSummarization(event.data as SummarizeTicketInput),
    )
  },
)
